import { useMemo, useState, useEffect, useCallback, memo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
    Loader2,
    Plus,
    LayoutTemplate,
    RefreshCw,
    Folder,
    Layers,
    Copy,
    User,
    Users,
    CheckCircle2,
    XCircle,
    UserSquare,
    Settings,
    Search,
} from 'lucide-react';
import { getMetaPages } from '@/services/api/connections';
import {
    getUtilityTemplateGroups,
    syncUtilityTemplates,
    createUtilityTemplate,
    type UtilityTemplateGroup,
    type UtilityTemplateCreatePayload,
    type UtilityTemplateImageSource,
    type UtilityTemplateStatus,
} from '@/services/api/utility-templates';
import { getUtilityTemplateCategories } from '@/services/api/utility-template-categories';
import CreateTemplateDialog, { type CreateTemplateInitialData } from './components/CreateTemplateDialog';
import UtilityTemplateMessengerPreview from './components/UtilityTemplateMessengerPreview';
import GroupDetailDialog from './components/GroupDetailDialog';
import ManageTemplateCategoriesDialog from './components/ManageTemplateCategoriesDialog';
import GroupCategoriesSelect from './components/GroupCategoriesSelect';
import FiltroUsuarioAdmin, { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';

// Categoria de PÁGINA (internal_category_id, herdada do país) e categoria de
// TEMPLATE (pasta livre, Requisito 4 do PRD) são conceitos distintos com
// lugares diferentes na UI: pasta de template fica na sidebar (navegação),
// categoria de página é um filtro na área principal (junto de busca/status).

const STATUS_TABS: { key: UtilityTemplateStatus | 'all'; label: string }[] = [
    { key: 'all', label: 'Todos' },
    { key: 'approved', label: 'Aprovados' },
    { key: 'pending', label: 'Pendentes' },
    { key: 'rejected', label: 'Rejeitados' },
    { key: 'failed', label: 'Falha' },
];

const OWNERSHIP_TABS: { key: 'all' | 'user'; label: string; icon: React.ElementType }[] = [
    { key: 'all', label: 'Todos os templates', icon: Users },
    { key: 'user', label: 'Seus templates', icon: User },
];

interface TemplateGroupCardProps {
    group: UtilityTemplateGroup;
    onClick: () => void;
    onDuplicate: () => void;
    duplicating: boolean;
}

const TemplateGroupCard = memo(function TemplateGroupCard({
    group,
    onClick,
    onDuplicate,
    duplicating,
}: TemplateGroupCardProps) {
    const approvedCount = group.status_counts?.approved ?? 0;
    const rejectedCount = group.status_counts?.rejected ?? 0;
    const creatorName = group.owner_name ?? 'Desconhecido';

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onClick();
            }}
            className="group relative flex flex-col h-full text-left rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-cyan-300 hover:shadow-lg cursor-pointer"
        >
            <button
                type="button"
                title="Duplicar template"
                onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate();
                }}
                disabled={duplicating}
                className="absolute top-2.5 left-2.5 z-10 h-7 w-7 flex items-center justify-center rounded-full bg-white/90 border border-gray-200 text-gray-500 hover:text-cyan-600 hover:border-cyan-300 shadow-sm disabled:opacity-50"
            >
                {duplicating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            </button>

            {/* Mesmo preview de chat Messenger do editor (Preview em Tempo
                Real) — templates só-texto aparecem como bolha de texto puro,
                sem card de imagem, igual ao que a Meta realmente renderiza.
                flex-1 absorve a diferença de altura entre "com imagem" e "só
                texto", empurrando o rodapé de infos sempre para o final do
                card (que por sua vez estica h-full até a linha do grid). */}
            <div className="pointer-events-none flex-1">
                <UtilityTemplateMessengerPreview
                    pageName={group.owner_name ?? undefined}
                    primaryTemplate={
                        group.image_url || group.title || group.subtitle
                            ? {
                                  title: group.title,
                                  subtitle: group.subtitle,
                                  template_name: group.template_name,
                                  image_url: group.image_url,
                                  thumbnail_url: group.thumbnail_url,
                                  buttons: group.buttons,
                              }
                            : null
                    }
                    compact
                />
            </div>

            <div className="flex flex-col gap-3 p-3.5 border-t border-gray-100 w-full mt-auto">
                <p
                    className="font-semibold text-sm text-gray-900 truncate"
                    title={group.template_name}
                >
                    {group.template_name}
                </p>

                <div className="space-y-1.5 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                        <UserSquare className="h-3.5 w-3.5 text-gray-400" />
                        <span className="truncate">
                            Criado por: <span className="font-medium">{creatorName}</span> (ID: {group.user_id})
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        Páginas Aprovadas: <span className="font-medium">{approvedCount}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <XCircle className="h-3.5 w-3.5 text-red-500" />
                        Páginas Recusadas: <span className="font-medium">{rejectedCount}</span>
                    </div>
                </div>

                {/* Categorias de TEMPLATE (pastas livres) — distinto do filtro de
                    categoria de página da sidebar. Só disponível para o dono do
                    grupo (biblioteca é compartilhada, mas associação é do autor). */}
                {group.is_mine && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <GroupCategoriesSelect group={group} />
                    </div>
                )}
            </div>
        </div>
    );
});

TemplateGroupCard.displayName = 'TemplateGroupCard';

// Wrapper que converte os handlers estáveis em closures para cada group.id
// Isso garante que o React.memo do TemplateGroupCard funcione corretamente.
interface MemoizedCardWrapperProps {
    group: UtilityTemplateGroup;
    onOpenGroup: (id: number) => void;
    onDuplicate: (group: UtilityTemplateGroup) => void;
    duplicatingGroupId: number | null;
}

const MemoizedCardWrapper = memo(function MemoizedCardWrapper({
    group,
    onOpenGroup,
    onDuplicate,
    duplicatingGroupId,
}: MemoizedCardWrapperProps) {
    const handleClick = useCallback(() => onOpenGroup(group.id), [onOpenGroup, group.id]);
    const handleDuplicate = useCallback(() => onDuplicate(group), [onDuplicate, group]);
    return (
        <TemplateGroupCard
            group={group}
            onClick={handleClick}
            onDuplicate={handleDuplicate}
            duplicating={duplicatingGroupId === group.id}
        />
    );
});

export default function UtilityTemplates() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [openGroupId, setOpenGroupId] = useState<number | null>(null);
    const [duplicateInitialData, setDuplicateInitialData] = useState<CreateTemplateInitialData | null>(null);
    const [duplicatingGroupId] = useState<number | null>(null);
    const [statusFilter, setStatusFilter] = useState<UtilityTemplateStatus | 'all'>('all');
    const [ownershipFilter, setOwnershipFilter] = useState<'all' | 'user'>('all');
    // A biblioteca já vem inteira para todos; o admin só ganha o recorte por
    // dono, feito aqui mesmo, e age em qualquer grupo como o dono dele.
    const { usuarioId: usuarioFiltro } = useFiltroUsuarioAdmin();
    const [templateCategoryFilter, setTemplateCategoryFilter] = useState<number | null>(null);
    const [pageCategoryFilter, setPageCategoryFilter] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleCount, setVisibleCount] = useState(24);
    const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);

    const { data: pages = [] } = useQuery({
        queryKey: ['meta-pages-all'],
        queryFn: getMetaPages,
        staleTime: 1000 * 60 * 5,
    });

    // Biblioteca compartilhada entre todos os clientes — o backend já
    // retorna 1 linha por grupo, com status_counts/category_ids agregados
    // (evita baixar cada submissão individual só pra contar/filtrar).
    const { data: groups = [], isLoading } = useQuery({
        queryKey: ['utility-template-groups'],
        queryFn: () => getUtilityTemplateGroups(),
        staleTime: 1000 * 30,
    });

    const categories = useMemo(() => {
        const map = new Map<number, string>();
        for (const p of pages) {
            if (p.internal_category_id && p.internal_category_name) {
                map.set(p.internal_category_id, p.internal_category_name);
            }
        }
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [pages]);

    // Categorias de TEMPLATE (pastas livres) — distinto das categorias de
    // página acima; nomeação livre pelo usuário, sem hierarquia/país.
    const { data: templateCategories = [] } = useQuery({
        queryKey: ['utility-template-categories'],
        queryFn: getUtilityTemplateCategories,
        staleTime: 1000 * 30,
    });

    // Grupos após ownership + categoria + busca (mas ANTES do filtro de
    // status) — é essa base que alimenta a contagem de cada aba de status.
    const groupsBeforeStatusFilter = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return groups.filter((group) => {
            if (ownershipFilter === 'user' && !group.is_mine) return false;
            if (usuarioFiltro !== null && group.user_id !== usuarioFiltro) return false;
            if (pageCategoryFilter !== null && !group.category_ids?.includes(pageCategoryFilter)) return false;
            if (templateCategoryFilter !== null && !group.utility_category_ids?.includes(templateCategoryFilter)) return false;
            if (term && !group.template_name.toLowerCase().includes(term)) return false;
            return true;
        });
    }, [groups, ownershipFilter, usuarioFiltro, pageCategoryFilter, templateCategoryFilter, searchTerm]);

    const counts = useMemo(() => {
        const result: Record<string, number> = {};
        for (const group of groupsBeforeStatusFilter) {
            for (const [status, qtd] of Object.entries(group.status_counts ?? {})) {
                if (qtd > 0) result[status] = (result[status] ?? 0) + 1;
            }
        }
        return result;
    }, [groupsBeforeStatusFilter]);

    const totalCount = groupsBeforeStatusFilter.length;

    const visibleGroups = useMemo(() => {
        return groupsBeforeStatusFilter
            .filter((group) => {
                if (statusFilter === 'all') return true;
                return (group.status_counts?.[statusFilter] ?? 0) > 0;
            })
            .sort((a, b) => (b.updated_at ?? b.created_at).localeCompare(a.updated_at ?? a.created_at));
    }, [groupsBeforeStatusFilter, statusFilter]);

    // Reseta a paginação ao mudar os filtros
    useEffect(() => {
        setVisibleCount(24);
    }, [statusFilter, templateCategoryFilter, pageCategoryFilter, searchTerm, ownershipFilter, usuarioFiltro]);

    const displayedGroups = visibleGroups.slice(0, visibleCount);

    const { mutate: create, isPending: creating } = useMutation({
        mutationFn: async ({
            payload,
            imageSource,
            secondaryPayload,
        }: {
            payload: UtilityTemplateCreatePayload;
            // Ausente no caso "só texto" (bloco Foto desabilitado).
            imageSource: UtilityTemplateImageSource | undefined;
            secondaryPayload?: UtilityTemplateCreatePayload;
        }) => {
            const result = await createUtilityTemplate(payload, imageSource);
            // Bloco "Texto" opcional — cria um 2º template independente (sem
            // imagem), mesmas páginas. Falha aqui não desfaz o principal
            // (já criado com sucesso); só reporta no toast de erro.
            if (secondaryPayload) {
                await createUtilityTemplate(secondaryPayload);
            }
            return result;
        },
        onSuccess: () => {
            toast({
                title: 'Templates Agendados',
                description: 'Seus templates foram salvos e serão enviados em breve.',
            });
            queryClient.invalidateQueries({ queryKey: ['utility-template-groups'] });
            setIsCreateOpen(false);
        },
        onError: () => {
            toast({ title: 'Erro ao criar template.', variant: 'destructive' });
        },
    });

    const { mutate: sync, isPending: syncing } = useMutation({
        mutationFn: syncUtilityTemplates,
        onSuccess: (data) => {
            toast({
                title: `${data.updated} de ${data.checked} templates atualizados.`,
                description: data.errors.length > 0 ? `${data.errors.length} erro(s) ao consultar a Meta.` : undefined,
                variant: data.errors.length > 0 ? 'destructive' : 'default',
            });
            queryClient.invalidateQueries({ queryKey: ['utility-template-groups'] });
        },
        onError: () => {
            toast({ title: 'Erro ao sincronizar status com a Meta.', variant: 'destructive' });
        },
    });

    const handleDuplicateClick = useCallback((groupToDuplicate: UtilityTemplateGroup) => {
        setDuplicateInitialData({
            template_name: `${groupToDuplicate.template_name}_copy`,
            source_language: groupToDuplicate.source_language,
            title: groupToDuplicate.title,
            subtitle: groupToDuplicate.subtitle,
            buttons: groupToDuplicate.buttons,
            image_url: groupToDuplicate.image_url,
            page_ids: [],
        });
        setIsCreateOpen(true);
    }, []);

    const handleCreate = useCallback((
        payload: UtilityTemplateCreatePayload,
        imageSource: UtilityTemplateImageSource | undefined,
        secondaryPayload?: UtilityTemplateCreatePayload
    ) => {
        create({ payload, imageSource, secondaryPayload });
    }, [create]);

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Biblioteca de Utility Templates</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Templates de mensagem do tipo Utility para Messenger.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => sync()}
                        disabled={syncing}
                    >
                        {syncing ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                        )}
                        Atualizar status Meta
                    </Button>
                    <Button
                        type="button"
                        onClick={() => {
                            setDuplicateInitialData(null);
                            setIsCreateOpen(true);
                        }}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Novo Template
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
                <aside className="space-y-4">
                    {/* Categorias de TEMPLATE (pastas livres, sem hierarquia/país) —
                        Requisito 4 do PRD. Navegação em árvore na lateral,
                        igual sempre foi. Categoria de PÁGINA virou filtro na
                        área principal (ver barra de busca/filtros abaixo). */}
                    <div className="rounded-xl border border-gray-200 bg-white p-2">
                        <div className="flex items-center justify-between px-2.5 pt-1.5 pb-2">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Pastas de template
                            </p>
                            <button
                                type="button"
                                onClick={() => setIsManageCategoriesOpen(true)}
                                className="p-1 text-gray-400 hover:text-cyan-600 rounded-md hover:bg-gray-50"
                                title="Gerenciar pastas"
                            >
                                <Settings className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <nav className="space-y-0.5">
                            <button
                                type="button"
                                onClick={() => setTemplateCategoryFilter(null)}
                                aria-current={templateCategoryFilter === null ? 'true' : undefined}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 ${
                                    templateCategoryFilter === null
                                        ? 'bg-gray-900 text-white font-medium shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                            >
                                <Layers className={`h-4 w-4 shrink-0 ${templateCategoryFilter === null ? 'text-cyan-300' : 'text-gray-400'}`} />
                                <span className="truncate">Todas as pastas</span>
                            </button>
                        </nav>
                        {templateCategories.length === 0 ? (
                            <p className="text-xs text-gray-400 px-2.5 pt-1.5">Nenhuma pasta criada ainda.</p>
                        ) : (
                            <nav className="space-y-0.5">
                                {templateCategories.map((cat) => {
                                    const isActiveCategory = templateCategoryFilter === cat.id;
                                    return (
                                        <button
                                            key={cat.id}
                                            type="button"
                                            onClick={() => setTemplateCategoryFilter(isActiveCategory ? null : cat.id)}
                                            aria-current={isActiveCategory ? 'true' : undefined}
                                            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 ${
                                                isActiveCategory
                                                    ? 'bg-gray-900 text-white font-medium shadow-sm'
                                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                            }`}
                                        >
                                            <Folder className={`h-4 w-4 shrink-0 ${isActiveCategory ? 'text-cyan-300' : 'text-gray-400'}`} />
                                            <span className="truncate">{cat.name}</span>
                                        </button>
                                    );
                                })}
                            </nav>
                        )}
                    </div>
                </aside>

                <div className="space-y-4 min-w-0">
                    <div
                        role="tablist"
                        aria-label="Filtrar por dono"
                        className="flex items-center gap-4 border-b border-gray-200"
                    >
                        {OWNERSHIP_TABS.map((tab) => {
                            const isActive = ownershipFilter === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setOwnershipFilter(tab.key)}
                                    className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium outline-none transition-all ${
                                        isActive
                                            ? 'border-gray-900 text-gray-900'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                    }`}
                                >
                                    <tab.icon className="h-4 w-4" />
                                    {tab.label}
                                </button>
                            );
                        })}
                        <FiltroUsuarioAdmin className="ml-auto w-56 py-1" />
                    </div>

                    {/* Busca por nome de template + filtro por categoria de PÁGINA
                        (internal_category_name, herdada do país) — antes era um
                        bloco de navegação na sidebar, virou filtro compacto aqui
                        junto com a busca. */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <div className="relative flex-1 min-w-0">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Buscar por nome do template..."
                                className="h-9 pl-8 text-sm"
                            />
                        </div>
                        <div className="w-full sm:w-56 shrink-0">
                            <SearchableSelect
                                value={pageCategoryFilter !== null ? String(pageCategoryFilter) : 'all'}
                                onValueChange={(v) => setPageCategoryFilter(v === 'all' ? null : Number(v))}
                                options={[
                                    { value: 'all', label: 'Todas as categorias de página' },
                                    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
                                ]}
                                placeholder="Categoria de página"
                                className="h-9 text-sm"
                            />
                        </div>
                    </div>

                    <div
                        role="tablist"
                        aria-label="Filtrar por status"
                        className="inline-flex flex-wrap items-center gap-1 rounded-xl border border-gray-200 bg-gray-100 p-1"
                    >
                        {STATUS_TABS.map((tab) => {
                            const count = tab.key === 'all' ? totalCount : counts[tab.key] ?? 0;
                            const isActive = statusFilter === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setStatusFilter(tab.key)}
                                    className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 ${
                                        isActive
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    {tab.label}
                                    <span
                                        className={`inline-flex items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                                            isActive ? 'bg-cyan-100 text-cyan-700' : 'bg-gray-200 text-gray-500'
                                        }`}
                                    >
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
                        </div>
                    ) : visibleGroups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2 border border-dashed border-gray-200 rounded-xl bg-white">
                            <LayoutTemplate className="h-8 w-8 opacity-40" />
                            <p className="text-sm text-gray-500">Nenhum utility template encontrado com esse filtro</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {displayedGroups.map((group) => (
                                    <MemoizedCardWrapper
                                        key={group.id}
                                        group={group}
                                        onOpenGroup={setOpenGroupId}
                                        onDuplicate={handleDuplicateClick}
                                        duplicatingGroupId={duplicatingGroupId}
                                    />
                                ))}
                            </div>
                            
                            {visibleCount < visibleGroups.length && (
                                <div className="flex justify-center pt-4 pb-8">
                                    <Button 
                                        variant="outline" 
                                        onClick={() => setVisibleCount((c) => c + 24)}
                                        className="bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-sm"
                                    >
                                        Carregar mais templates
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <CreateTemplateDialog
                open={isCreateOpen}
                onOpenChange={(open) => {
                    setIsCreateOpen(open);
                    if (!open) setDuplicateInitialData(null);
                }}
                pages={pages}
                onSubmit={handleCreate}
                isPending={creating}
                initialData={duplicateInitialData}
            />

            <GroupDetailDialog
                groupId={openGroupId}
                onOpenChange={(open) => setOpenGroupId(open ? openGroupId : null)}
                pages={pages}
            />

            <ManageTemplateCategoriesDialog
                open={isManageCategoriesOpen}
                onOpenChange={setIsManageCategoriesOpen}
            />
        </div>
    );
}
